import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import styled from 'styled-components';
import axios from 'axios';
import { toast } from 'react-toastify';

const Page = styled.div`
  display: flex;
  min-height: 100vh;
  background: #f4f6f8;
`;

const Rail = styled.aside`
  width: 280px;
  flex: none;
  background: #fff;
  border-right: 1px solid #e0e4e8;
  padding: 1.5rem 1rem;
  overflow-y: auto;

  @media (max-width: 900px) {
    width: 100%;
    border-right: none;
    border-bottom: 1px solid #e0e4e8;
  }
`;

const BackLink = styled(Link)`
  color: #005eb8;
  text-decoration: none;
  font-size: 0.85rem;
  &:hover { text-decoration: underline; }
`;

const RailTitle = styled.h2`
  font-size: 1rem;
  margin: 0.75rem 0 0.25rem;
`;

const RailSub = styled.p`
  font-size: 0.8rem;
  color: #666;
  margin: 0 0 1.25rem;
  line-height: 1.4;
`;

const BrandingBox = styled.div`
  background: #f8f9fa;
  border: 1px solid #e0e4e8;
  border-radius: 8px;
  padding: 0.85rem 1rem;
  margin-bottom: 1.25rem;
`;

const BrandingLabel = styled.label`
  display: block;
  font-size: 0.75rem;
  font-weight: bold;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: #666;
  margin-bottom: 0.4rem;
`;

const BrandingRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.6rem;
`;

const RangeInput = styled.input`
  flex: 1;
`;

const SizeReadout = styled.span`
  font-size: 0.85rem;
  font-weight: bold;
  color: #005eb8;
  min-width: 44px;
  text-align: right;
`;

const StepList = styled.ol`
  list-style: none;
  margin: 0;
  padding: 0;
  position: relative;
  &::before {
    content: "";
    position: absolute;
    left: 15px;
    top: 14px;
    bottom: 14px;
    width: 2px;
    background: #e0e4e8;
  }
`;

const Step = styled.li`
  position: relative;
  display: flex;
  gap: 10px;
  padding: 8px 6px;
  border-radius: 6px;
  cursor: pointer;
  align-items: flex-start;
  background: ${p => (p.$active ? '#e4eefb' : 'transparent')};
  &:hover { background: ${p => (p.$active ? '#e4eefb' : '#f4f6f8')}; }
`;

const Node = styled.div`
  flex: none;
  width: 26px;
  height: 26px;
  border-radius: 50%;
  background: #fff;
  border: 2px solid ${p => (p.$active ? '#005eb8' : '#ccd3d9')};
  color: ${p => (p.$active ? '#005eb8' : '#888')};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: bold;
  z-index: 1;
`;

const StepLabel = styled.div`
  padding-top: 3px;
  min-width: 0;
`;

const StepName = styled.div`
  font-size: 0.85rem;
  font-weight: 600;
  color: ${p => (p.$active ? '#004a94' : '#222')};
`;

const BranchTag = styled.span`
  display: inline-block;
  font-size: 0.65rem;
  font-weight: bold;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  padding: 1px 6px;
  border-radius: 999px;
  margin-bottom: 3px;
  color: ${p => (p.$tone === 'good' ? '#1a7a4c' : '#b7791f')};
  background: ${p => (p.$tone === 'good' ? '#e4f5eb' : '#fff2dd')};
`;

const BranchGroup = styled.div`
  margin-left: 26px;
  padding-left: 10px;
  border-left: 2px dashed #e0e4e8;
`;

const StandaloneNote = styled.div`
  font-size: 0.7rem;
  color: #888;
  font-style: italic;
  margin: 10px 4px 2px;
`;

const Main = styled.main`
  flex: 1;
  min-width: 0;
  padding: 1.5rem 2rem 3rem;
`;

const TopBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.25rem;
  flex-wrap: wrap;
  gap: 0.75rem;
`;

const UnsavedBadge = styled.span`
  font-size: 0.75rem;
  color: #b7791f;
  background: #fff2dd;
  padding: 2px 8px;
  border-radius: 999px;
  margin-left: 0.6rem;
  font-weight: normal;
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 0.6rem;
`;

const Button = styled.button`
  padding: 8px 16px;
  background-color: #005eb8;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.9rem;
  &:hover { background-color: #004a94; }
  &:disabled { background-color: #99b9d6; cursor: not-allowed; }
  &.secondary { background: #fff; color: #005eb8; border: 1px solid #005eb8; }
  &.secondary:hover { background: #f0f6fc; }
`;

const Trigger = styled.p`
  font-size: 0.85rem;
  color: #666;
  background: #f8f9fa;
  border: 1px solid #e0e4e8;
  border-radius: 6px;
  padding: 0.6rem 0.9rem;
  margin: 0 0 1.25rem;
`;

const Columns = styled.div`
  display: flex;
  gap: 1.5rem;
  align-items: flex-start;
  @media (max-width: 1100px) {
    flex-direction: column;
  }
`;

const FormCol = styled.div`
  flex: 1;
  min-width: 0;
`;

const PreviewCol = styled.div`
  flex: 1;
  min-width: 0;
  position: sticky;
  top: 1.5rem;
`;

const Field = styled.div`
  margin-bottom: 1rem;
`;

const FieldLabel = styled.label`
  display: block;
  font-size: 0.85rem;
  font-weight: bold;
  margin-bottom: 0.35rem;
`;

const TextInput = styled.input`
  width: 100%;
  padding: 8px 10px;
  border: 1px solid #ccc;
  border-radius: 4px;
  box-sizing: border-box;
  font-size: 0.9rem;
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 8px 10px;
  border: 1px solid #ccc;
  border-radius: 4px;
  box-sizing: border-box;
  font-size: 0.9rem;
  min-height: 90px;
  font-family: inherit;
  resize: vertical;
`;

const CharCount = styled.div`
  font-size: 0.7rem;
  color: #999;
  text-align: right;
  margin-top: 2px;
`;

const Placeholders = styled.p`
  font-size: 0.78rem;
  color: #666;
  margin: -0.4rem 0 1.25rem;
  code { background: #f0f2f4; padding: 1px 5px; border-radius: 3px; }
`;

const VariantToggle = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-bottom: 0.75rem;
`;

const VariantButton = styled.button`
  padding: 5px 12px;
  border-radius: 999px;
  border: 1px solid #ccd3d9;
  background: ${p => (p.$active ? '#005eb8' : '#fff')};
  color: ${p => (p.$active ? '#fff' : '#333')};
  font-size: 0.8rem;
  cursor: pointer;
`;

const PreviewFrame = styled.iframe`
  width: 100%;
  border: 1px solid #e0e4e8;
  border-radius: 8px;
  background: #fff;
  min-height: 400px;
`;

const buildStepGroups = (templates) => {
  const main = templates.filter(t => !t.standalone);
  const standalone = templates.filter(t => t.standalone);
  const orders = [...new Set(main.map(t => t.order))].sort((a, b) => a - b);
  const groups = orders.map(order => ({
    order,
    items: main.filter(t => t.order === order),
  }));
  return { groups, standalone };
};

const EmailTemplates = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem('adminToken');
  const api = useMemo(() => axios.create({
    baseURL: process.env.REACT_APP_BACKEND_URL,
    headers: { Authorization: `Bearer ${token}` }
  }), [token]);

  useEffect(() => {
    if (!token) navigate('/');
  }, [token, navigate]);

  const [templates, setTemplates] = useState([]);
  const [savedValues, setSavedValues] = useState({});
  const [formValues, setFormValues] = useState({});
  const [branding, setBranding] = useState({ logoSize: 72 });
  const [savedLogoSize, setSavedLogoSize] = useState(72);
  const [selectedId, setSelectedId] = useState(null);
  const [variant, setVariant] = useState('digital');
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const previewTimer = useRef(null);
  const brandingTimer = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/admins/email-templates');
      const { templates: t, branding: b } = res.data;
      setTemplates(t);
      const vals = {};
      t.forEach(tpl => { vals[tpl.id] = tpl.values; });
      setFormValues(vals);
      setSavedValues(vals);
      setBranding(b);
      setSavedLogoSize(b.logoSize);
      if (!selectedId) setSelectedId(t[0]?.id);
    } catch (err) {
      toast.error('Failed to load email templates.');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const selected = templates.find(t => t.id === selectedId);
  const values = formValues[selectedId] || {};
  const isDirty = selected && JSON.stringify(values) !== JSON.stringify(savedValues[selectedId]);
  const isBrandingDirty = branding.logoSize !== savedLogoSize;

  const fetchPreview = useCallback(async () => {
    if (!selectedId) return;
    setPreviewLoading(true);
    try {
      const res = await api.post(`/admins/email-templates/${selectedId}/preview`, {
        values,
        branding,
        variant: selected?.id === 'ready' ? variant : undefined,
      });
      setPreview(res.data.preview);
    } catch (err) {
      // Keep the last good preview on transient errors.
    } finally {
      setPreviewLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, selectedId, values, branding, variant]);

  useEffect(() => {
    clearTimeout(previewTimer.current);
    previewTimer.current = setTimeout(fetchPreview, 350);
    return () => clearTimeout(previewTimer.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId, JSON.stringify(values), branding.logoSize, variant]);

  const handleFieldChange = (key, value) => {
    setFormValues(prev => ({ ...prev, [selectedId]: { ...prev[selectedId], [key]: value } }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await api.put(`/admins/email-templates/${selectedId}`, { values });
      setSavedValues(prev => ({ ...prev, [selectedId]: res.data.values }));
      setFormValues(prev => ({ ...prev, [selectedId]: res.data.values }));
      setTemplates(prev => prev.map(t => t.id === selectedId ? { ...t, values: res.data.values, isCustomized: true } : t));
      toast.success('Email saved.');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save email.');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm(`Reset "${selected.label}" back to its original wording? This can't be undone.`)) return;
    setSaving(true);
    try {
      const res = await api.post(`/admins/email-templates/${selectedId}/reset`);
      setSavedValues(prev => ({ ...prev, [selectedId]: res.data.values }));
      setFormValues(prev => ({ ...prev, [selectedId]: res.data.values }));
      setTemplates(prev => prev.map(t => t.id === selectedId ? { ...t, values: res.data.values, isCustomized: false } : t));
      toast.success('Reset to the original wording.');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to reset email.');
    } finally {
      setSaving(false);
    }
  };

  const handleLogoSizeChange = (value) => {
    setBranding({ logoSize: Number(value) });
  };

  const saveBranding = useCallback(async (logoSize) => {
    try {
      const res = await api.put('/admins/email-templates/branding', { logoSize });
      setSavedLogoSize(res.data.branding.logoSize);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save logo size.');
    }
  }, [api]);

  useEffect(() => {
    if (branding.logoSize === savedLogoSize) return;
    clearTimeout(brandingTimer.current);
    brandingTimer.current = setTimeout(() => saveBranding(branding.logoSize), 500);
    return () => clearTimeout(brandingTimer.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branding.logoSize]);

  const { groups, standalone } = useMemo(() => buildStepGroups(templates), [templates]);

  if (loading) {
    return <Page><Main><p>Loading email templates...</p></Main></Page>;
  }

  return (
    <Page>
      <Rail>
        <BackLink to="/dashboard">&larr; Back to Dashboard</BackLink>
        <RailTitle>Customer Emails</RailTitle>
        <RailSub>Every automated email, in the order a real order moves through them.</RailSub>

        <BrandingBox>
          <BrandingLabel htmlFor="logo-size">Logo size (all emails)</BrandingLabel>
          <BrandingRow>
            <RangeInput
              id="logo-size"
              type="range"
              min="32"
              max="160"
              value={branding.logoSize}
              onChange={e => handleLogoSizeChange(e.target.value)}
            />
            <SizeReadout>{branding.logoSize}px</SizeReadout>
          </BrandingRow>
          {isBrandingDirty && <UnsavedBadge style={{ marginLeft: 0, marginTop: 6, display: 'inline-block' }}>Saving…</UnsavedBadge>}
        </BrandingBox>

        <StepList>
          {groups.map(group => (
            group.items.length > 1 ? (
              <BranchGroup key={group.order}>
                {group.items.map(tpl => (
                  <Step key={tpl.id} $active={tpl.id === selectedId} onClick={() => setSelectedId(tpl.id)}>
                    <Node $active={tpl.id === selectedId}>{tpl.order}</Node>
                    <StepLabel>
                      {tpl.branchLabel && <BranchTag $tone={tpl.branch}>{tpl.branchLabel}</BranchTag>}
                      <div><StepName $active={tpl.id === selectedId}>{tpl.label}</StepName></div>
                    </StepLabel>
                  </Step>
                ))}
              </BranchGroup>
            ) : (
              <Step key={group.items[0].id} $active={group.items[0].id === selectedId} onClick={() => setSelectedId(group.items[0].id)}>
                <Node $active={group.items[0].id === selectedId}>{group.order}</Node>
                <StepLabel>
                  <StepName $active={group.items[0].id === selectedId}>{group.items[0].label}</StepName>
                </StepLabel>
              </Step>
            )
          ))}
          {standalone.length > 0 && <StandaloneNote>Not part of the order timeline:</StandaloneNote>}
          {standalone.map(tpl => (
            <Step key={tpl.id} $active={tpl.id === selectedId} onClick={() => setSelectedId(tpl.id)}>
              <Node $active={tpl.id === selectedId}>&bull;</Node>
              <StepLabel>
                <StepName $active={tpl.id === selectedId}>{tpl.label}</StepName>
              </StepLabel>
            </Step>
          ))}
        </StepList>
      </Rail>

      <Main>
        {selected && (
          <>
            <TopBar>
              <div>
                <h1 style={{ margin: 0, display: 'inline' }}>{selected.label}</h1>
                {isDirty && <UnsavedBadge>Unsaved changes</UnsavedBadge>}
                {selected.isCustomized && !isDirty && <UnsavedBadge style={{ color: '#1a7a4c', background: '#e4f5eb' }}>Customized</UnsavedBadge>}
              </div>
              <ButtonRow>
                <Button className="secondary" onClick={handleReset} disabled={saving || !selected.isCustomized}>Reset to Default</Button>
                <Button onClick={handleSave} disabled={saving || !isDirty}>{saving ? 'Saving...' : 'Save'}</Button>
              </ButtonRow>
            </TopBar>

            <Trigger>Fires when: {selected.trigger}</Trigger>

            <Columns>
              <FormCol>
                {selected.fields.map(field => (
                  <Field key={field.key}>
                    <FieldLabel htmlFor={`field-${field.key}`}>{field.label}</FieldLabel>
                    {field.type === 'textarea' ? (
                      <TextArea
                        id={`field-${field.key}`}
                        maxLength={field.maxLength}
                        value={values[field.key] || ''}
                        onChange={e => handleFieldChange(field.key, e.target.value)}
                      />
                    ) : (
                      <TextInput
                        id={`field-${field.key}`}
                        type="text"
                        maxLength={field.maxLength}
                        value={values[field.key] || ''}
                        onChange={e => handleFieldChange(field.key, e.target.value)}
                      />
                    )}
                    <CharCount>{(values[field.key] || '').length} / {field.maxLength}</CharCount>
                  </Field>
                ))}
                <Placeholders>
                  Use <code>**bold**</code> for emphasis.
                  {selected.placeholders?.length > 0 && (
                    <> Available placeholders: {selected.placeholders.map(p => <code key={p}>{`{{${p}}}`}</code>)}</>
                  )}
                </Placeholders>
              </FormCol>

              <PreviewCol>
                {selected.id === 'ready' && (
                  <VariantToggle>
                    <VariantButton $active={variant === 'digital'} onClick={() => setVariant('digital')}>Email / Download</VariantButton>
                    <VariantButton $active={variant === 'post'} onClick={() => setVariant('post')}>Post</VariantButton>
                  </VariantToggle>
                )}
                <div style={{ fontSize: '0.85rem', color: '#666', marginBottom: '0.4rem' }}>
                  <strong>Subject:</strong> {preview?.subject || '...'} {previewLoading && '(updating…)'}
                </div>
                <PreviewFrame
                  title="Email preview"
                  srcDoc={preview?.html || ''}
                  onLoad={(e) => {
                    try {
                      const h = e.target.contentWindow.document.documentElement.scrollHeight;
                      e.target.style.height = Math.max(h, 300) + 'px';
                    } catch (err) { /* cross-origin guard, not expected for srcDoc */ }
                  }}
                />
              </PreviewCol>
            </Columns>
          </>
        )}
      </Main>
    </Page>
  );
};

export default EmailTemplates;
